import { useCallback, useMemo } from 'react';
import { Button } from '@accure/ui';
import { endOfDay, startOfDay, subQuarters, subWeeks } from 'date-fns';
import Highcharts from 'highcharts';

import { formatDate } from '@/utils/format/date';
import { formatNumber, formatPercentage } from '@/utils/format/number';
import { getUserTimezone } from '@/utils/timezone';
import { Chart } from '@/warranty-v3/components/chart';
import { Card, CardContent } from '@/warranty-v3/components/ui/card';
import type {
  MetricUnit,
  TimeBasedMetrics,
} from '@/warranty-v3/entities/metric';
import {
  formatMetricValue,
  metricValueAxisLabel,
} from '@/warranty-v3/utils/format-metric-value';
import { colorFromMetricId } from '@/warranty-v3/utils/metrics-serie-colors';

const YAXIS_PERCENT = 0;
const YAXIS_LD = 1;
const YAXIS_ABSOLUTE = 2;

function yAxisForValueSeries(unit: MetricUnit): number {
  return unit === 'percent' ? YAXIS_PERCENT : YAXIS_ABSOLUTE;
}

export type MetricChartProps = {
  timeBasedMetrics: TimeBasedMetrics;
};
export function MetricChart({ timeBasedMetrics }: MetricChartProps) {
  const chartOptions = useMemo<Highcharts.Options>(
    function () {
      const allMetrics = timeBasedMetrics.groups.flatMap(
        (group) => group.metrics,
      );

      const unitBySeriesName = new Map<string, MetricUnit>();

      const series: Highcharts.SeriesOptionsType[] = allMetrics.flatMap(
        function (metric) {
          const series: Highcharts.SeriesOptionsType[] = [];
          const sortByDate = (
            [dateA]: [Date, number],
            [dateB]: [Date, number],
          ) => dateA.getTime() - dateB.getTime();

          if (metric.valueSeries) {
            const sortedPoints = metric.valueSeries.points.sort(sortByDate);
            unitBySeriesName.set(metric.name, metric.unit);
            series.push({
              name: metric.name,
              type: 'line',
              yAxis: yAxisForValueSeries(metric.unit),
              color: colorFromMetricId[metric.id],
              marker: { enabled: false },
              data: sortedPoints.map(([date, value]) => [
                date.getTime(),
                value,
              ]),
            });
          }

          if (metric.capedLDSeries) {
            const sortedPoints = metric.capedLDSeries.points.sort(sortByDate);
            series.push({
              name: `LD ${metric.name}`,
              type: 'line',
              marker: { enabled: false },
              yAxis: YAXIS_LD,
              color: colorFromMetricId[metric.id],
              dashStyle: 'Dot',
              data: sortedPoints.map(([date, value]) => [
                date.getTime(),
                value / 1000,
              ]),
            });
          }

          return series;
        },
      );

      const showPercentYAxis = series.some(
        (s) => s.yAxis === YAXIS_PERCENT || s.yAxis === undefined,
      );
      const showLDYAxis = series.some((s) => s.yAxis === YAXIS_LD);
      const showAbsoluteYAxis = series.some((s) => s.yAxis === YAXIS_ABSOLUTE);

      const absoluteUnit: MetricUnit =
        allMetrics.find((m) => m.unit !== 'percent')?.unit ?? 'MWh';

      return {
        series,
        chart: {
          animation: false,
          type: 'line',
          zooming: {
            type: 'x',
          },
          resetZoomButton: {
            theme: { style: { display: 'none' } },
          },
          height: 350,
          spacingRight: 40,
        },
        credits: {
          enabled: false,
        },
        time: {
          timezone: getUserTimezone(),
        },
        title: {
          text: undefined,
        },
        tooltip: {
          useHTML: true,
          xDateFormat: '%Y-%m-%d %H:%M',
          headerFormat:
            '<span style="font-size: 11px; color: #6b7280;">{point.key}</span><br/>',
          pointFormatter() {
            const value = this.y ?? 0;
            let formattedValue: string;

            if (this.series.options.yAxis === YAXIS_LD) {
              formattedValue = `${formatNumber(value, 0, 2)}k$`;
            } else {
              const unit = unitBySeriesName.get(this.series.name) ?? 'percent';
              formattedValue = formatMetricValue(value, unit);
            }

            return `<span style="font-size: 13px; font-weight: 600;">${this.series.name}</span><br/><span style="font-size: 15px; font-weight: 700; color: ${this.color};">${formattedValue}</span>`;
          },
        },
        plotOptions: {
          series: {
            animation: false,
            stickyTracking: false,
            states: {
              hover: {
                lineWidth: 3,
              },
              inactive: {
                opacity: 0.15,
              },
            },
          },
        },
        xAxis: {
          type: 'datetime',
          crosshair: {
            width: 1,
            color: '#9ca3af',
            dashStyle: 'Dash',
          },
          title: {
            text: undefined,
          },
          labels: {
            formatter() {
              return formatDate(new Date(this.value));
            },
          },
        },
        yAxis: [
          {
            enabled: showPercentYAxis,
            title: {
              text: showPercentYAxis ? 'Percentage' : null,
            },
            softMin: 0.5,
            softMax: 1,
            labels: {
              formatter() {
                return formatPercentage(Number(this.value), 0, 2);
              },
            },
          },
          {
            enabled: showLDYAxis,
            title: {
              text: showLDYAxis ? 'LD in k$' : null,
            },
            opposite: true,
            softMin: 0,
            endOnTick: true,
            labels: {
              formatter() {
                return `${formatNumber(Number(this.value), 0, 1)}k$`;
              },
            },
          },
          {
            enabled: showAbsoluteYAxis,
            title: {
              text: showAbsoluteYAxis
                ? metricValueAxisLabel(absoluteUnit)
                : null,
            },
            opposite: !showLDYAxis,
            softMin: 0,
            endOnTick: true,
            labels: {
              formatter() {
                return formatMetricValue(
                  Number(this.value),
                  absoluteUnit,
                  0,
                  1,
                );
              },
            },
          },
        ],
      } satisfies Highcharts.Options;
    },
    [timeBasedMetrics],
  );

  const zoomAll = useCallback(function () {
    for (const chart of Highcharts.charts) {
      // @ts-expect-error id is not typed but is here
      if (!chart || chart.options.id !== 'metrics-chart') {
        continue;
      }

      chart.xAxis[0].setExtremes(undefined, undefined, true, true);
      break;
    }
  }, []);

  const zoomWarrantyYear = useCallback(
    function () {
      const start = startOfDay(timeBasedMetrics.start);
      const end = endOfDay(timeBasedMetrics.end);

      for (const chart of Highcharts.charts) {
        // @ts-expect-error id is not typed but is here
        if (!chart || chart.options.id !== 'metrics-chart') {
          continue;
        }

        chart.xAxis[0].setExtremes(start.getTime(), end.getTime(), true, true);
        break;
      }
    },
    [timeBasedMetrics.start, timeBasedMetrics.end],
  );

  const zoomLastQuarter = useCallback(
    function () {
      const start = startOfDay(subQuarters(timeBasedMetrics.end, 1));
      const end = endOfDay(timeBasedMetrics.end);

      for (const chart of Highcharts.charts) {
        // @ts-expect-error id is not typed but is here
        if (!chart || chart.options.id !== 'metrics-chart') {
          continue;
        }

        chart.xAxis[0].setExtremes(start.getTime(), end.getTime(), true, true);
        break;
      }
    },
    [timeBasedMetrics.end],
  );

  const zoomLastWeek = useCallback(
    function () {
      const start = startOfDay(subWeeks(timeBasedMetrics.end, 1));
      const end = endOfDay(timeBasedMetrics.end);

      for (const chart of Highcharts.charts) {
        // @ts-expect-error id is not typed but is here
        if (!chart || chart.options.id !== 'metrics-chart') {
          continue;
        }

        chart.xAxis[0].setExtremes(start.getTime(), end.getTime(), true, true);
        break;
      }
    },
    [timeBasedMetrics.end],
  );

  const showChart = chartOptions.series && chartOptions.series.length > 0;

  if (!showChart) {
    return null;
  }

  return (
    <Card className="overflow-hidden border-gray-200 bg-white pb-0">
      <CardContent className="flex justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-900">KPIs over time</h2>
        <div className="shadow-xs inline-flex w-fit -space-x-px rounded-md rtl:space-x-reverse">
          <Button
            variant="outline"
            className="rounded-none rounded-s-md shadow-none focus-visible:z-10"
            onClick={zoomAll}
          >
            All
          </Button>

          <Button
            variant="outline"
            className="rounded-none shadow-none focus-visible:z-10"
            onClick={zoomWarrantyYear}
          >
            Warranty Period
          </Button>

          <Button
            variant="outline"
            className="rounded-none shadow-none focus-visible:z-10"
            onClick={zoomLastQuarter}
          >
            Last Quarter
          </Button>
          <Button
            variant="outline"
            className="rounded-none rounded-e-md shadow-none focus-visible:z-10"
            onClick={zoomLastWeek}
          >
            Last Week
          </Button>
        </div>
      </CardContent>
      <CardContent className="p-0">
        <Chart id="metrics-chart" options={chartOptions} />
      </CardContent>
    </Card>
  );
}
